import type { ID } from "../types";
import type { MultiSelect } from "../multiselect";
import { collapseSelection, CollapsePositionFirst } from "./collapse-strategy";
import type {
  CollapseOptions,
  CollapsedNode,
  PositionStrategy,
} from "./collapse-strategy";

export class CollapseManager<Data, Meta = unknown, GroupMeta = unknown> {
  private orderMap = new Map<ID, number>();
  private counter = 0;

  constructor(private ms: MultiSelect<Data, Meta, GroupMeta>) {
    ms.subscribe((_state, diff) => {
      for (const id of diff.added) {
        this.orderMap.set(id, this.counter++);
      }
      for (const id of diff.removed) {
        this.orderMap.delete(id);
      }
    });
  }

  public getCollapsedSelection(
    options: CollapseOptions = {}
  ): CollapsedNode<Data, Meta, GroupMeta>[] {
    const filteredOnly = options.filteredOnly;
    const positionStrategy: PositionStrategy =
      options.positionStrategy ?? CollapsePositionFirst;

    const tree = this.ms.getGroupTree({ filter: { filteredOnly } });

    const collapsed = collapseSelection(tree, this.orderMap, {
      filteredOnly,
      positionStrategy,
    });

    const treeItemIds = new Set<ID>();
    for (const node of tree) {
      for (const item of node.getItems({ filteredOnly })) {
        treeItemIds.add(item.id);
      }
    }
    const selectedItems = this.ms.getSelectedItems({ filteredOnly });
    for (const item of selectedItems) {
      if (!treeItemIds.has(item.id)) {
        const order = this.orderMap.get(item.id) ?? Number.MAX_SAFE_INTEGER;
        collapsed.push({ type: "item", node: item, order });
      }
    }

    return collapsed.sort((a, b) => a.order - b.order);
  }
}
